/**
 * Board helpers for the PixiJS renderer (board-gl.ts): hex geometry, the colour
 * palette, ship sizing, sprite URLs, fog visibility, the minimap (a small Canvas2D
 * HUD thumbnail kept on 2D by design), and the BoardView/BoardOpts contract the UI
 * talks to.
 *
 * Coordinates: "down-positive" world units — rows increase downward (1.5·HEX·r),
 * odd-r pointy-top hexes (matches rules/board.ts hexCenter with y negated).
 */
import { visibleOnBoard, tileInView } from '../rules/fog';
import { FLAG_ANCHOR } from './ship-flag-meta';
import type { GameState, Ship, ShipType, Side } from '../rules/state';

export const HEX = 26; // world units (hex "radius")
export const SQ3 = Math.sqrt(3);
export const BORDER = 1; // decorative water rows drawn above/below the play area

// Isometric foreshortening. The board is a top-down odd-r hex grid; to sit under
// the 3/4-view ship / tile renders it is tilted back by compressing the vertical
// (depth) world axis. TILE_ASPECT is the native width/height of the isometric hex
// tile art (grass_tile.png ≈ 0.99); ISO_Y is the factor that makes a regular
// pointy-top hex (aspect √3/2 ≈ 0.87) draw at that aspect, so the tiles tessellate
// undistorted and the plane recedes. Applied to EVERY world-Y in the board
// renderer (hc, extentOf, hex polygons, foam/beach, selection). NOT applied to the
// minimap (a top-down thumbnail by design) or to upright billboards (ships, trees,
// mountains, flags) — those keep full height and merely sit at the foreshortened
// positions, so they stand up on the tilted ground.
export const TILE_ASPECT = 0.99;
export const ISO_Y = (SQ3 / 2) / TILE_ASPECT; // ≈ 0.875
export const FOG_FILL = 'rgba(4,12,24,0.45)'; // crisp shroud over everything not in view (minimap only)

export const PALETTE = {
  outOfBounds: '#41505c', // dull grey-blue "ice" backdrop shown beyond the map hull
  land: '#558f43',
  landAlt: '#4d853c',
  sand: '#e0ca8d',
  labelBg: 'rgba(16,30,52,0.82)',
  labelEdge: 'rgba(240,228,190,0.6)',
  labelText: '#ffffff',
};
export const sideColor = (s: Side): string => (s === 'R' ? '#c0392b' : '#d4a017');
// Minimap palette: the same real-map hues, but pushed brighter / more saturated so
// every tile type and team reads distinctly at thumbnail size on the dark sea+fog.
// (sideColor's flag reds/golds are too dark and muddy when shrunk to a few px.)
export const MINI = {
  sea: '#2a6a86', // the real-map sea hue (SEA_FILL) but darkened for thumbnail contrast — land/cities/ships read clearly on top
  land: '#5aa046', // ← real land #558f43, lifted in lightness
  cityR: '#ef5340', // ← real flag #c0392b, brighter + more saturated
  cityY: '#f4c220', // ← real flag #d4a017, brighter + more saturated
  cityNeutral: '#efe6c8',
  shipR: '#ff8c7c',
  shipY: '#ffd76e',
};
export const miniCity = (s: Side): string => (s === 'R' ? MINI.cityR : MINI.cityY);
// light team tints — for text on the dark label plate (the saturated sideColor reads too heavy)
export const sideColorLight = (s: Side): string => (s === 'R' ? 'hsl(8, 100%, 82%)' : 'hsl(44, 100%, 78%)');

export const SHIP_TYPES: ShipType[] = ['CV', 'BB', 'CR', 'SS', 'DD', 'PT', 'AT', 'TR', 'FT'];

// On-board world width factor (× HEX*1.55). Tuned so capital ships visibly
// dwarf escorts/transports per the manual proportions (BB/CV ≫ PT).
export const SHIP_SCALE: Record<ShipType, number> = {
  CV: 1.45, BB: 1.42, CR: 1.2, SS: 0.9, DD: 1.08, PT: 0.62, AT: 0.92, TR: 0.84, FT: 1.28,
};

// ---- realistic ship art (public/assets/ship-sprites, built by
// scripts/prep-ship-sprites.mjs from art/sprites-new) ----------------------
// Each class has a -left and -right facing render (flagless by design); on the
// board Red sails right and Yellow left, so the fleets steam toward each
// other. Every ship flies the SAME white ensign sprite (art/ensign-*.png),
// composited at the class's mast anchor and tinted with the side colour at
// draw time — one cloth, any livery, identical flag size on every hull.
// FT (a stone fort) stays on the vector sprites; a submerged sub is the same
// submarine render drawn ghosted by the renderers.
export const SHIP_ART: Partial<Record<ShipType, string>> = {
  CV: 'carrier', BB: 'battleship', CR: 'cruiser', DD: 'destroyer',
  PT: 'patrolboat', SS: 'submarine', AT: 'armoured-transportship', TR: 'transportship',
};
export type Facing = 'left' | 'right';
export const sideFacing = (side: Side): Facing => (side === 'R' ? 'right' : 'left');
export const shipArtUrl = (type: ShipType, facing: Facing): string | null =>
  SHIP_ART[type] ? `assets/ship-sprites/${SHIP_ART[type]}-${facing}.png` : null;

export const ENSIGN_WU = 7; // ensign cloth width in world units — same on every hull
export const ensignUrl = (facing: Facing): string => `assets/ship-sprites/ensign-${facing}.png`;
export interface EnsignLayout {
  /** pole-top corner of the cloth, offset from the sprite centre */
  fx: number; fy: number;
  /** cloth size */
  fw: number; fh: number;
  /** team-glyph centre + radius (on the cloth) */
  gx: number; gy: number; gr: number;
}
/** Where/how big the shared ensign draws on a ship sprite rendered w×h (any
 *  unit): anchored at the class's mast point, sized to ENSIGN_WU world units
 *  via the class's world width — so a patrol boat and a battleship fly the
 *  same cloth. The pole side faces the bow (cloth streams aft): right-facing
 *  ships hang the cloth LEFT of the anchor. `aspect` = ensign texture w/h.
 *  Null for classes without realistic art (the fort). */
export function ensignLayout(type: ShipType, facing: Facing, w: number, h: number, aspect: number): EnsignLayout | null {
  const name = SHIP_ART[type];
  const a = name ? FLAG_ANCHOR[`${name}-${facing}`] : undefined;
  if (!a) return null;
  const fw = (ENSIGN_WU / (HEX * 1.55 * (SHIP_SCALE[type] ?? 1))) * w;
  const fh = fw / aspect;
  const fx = (a.ax - 0.5) * w;
  const fy = (a.ay - 0.5) * h;
  return { fx, fy, fw, fh, gx: fx + (facing === 'right' ? -fw / 2 : fw / 2), gy: fy + fh * 0.52, gr: fh * 0.3 };
}

export function shipSpriteUrl(s: Ship): string {
  const side = s.side === 'R' ? 'r' : 'y';
  // A submerged sub is the SAME realistic render, drawn ghosted (translucent +
  // cold tint, no ensign) by the renderers — not a separate sprite.
  return shipArtUrl(s.type, sideFacing(s.side)) ?? `assets/sprites/ship_${s.type.toLowerCase()}_${side}.png`;
}

export function hexCorners(radius: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 30);
    pts.push([radius * Math.cos(a), radius * Math.sin(a)]);
  }
  return pts;
}

/** odd-r neighbour across geometric hex edge i (0=E,1=NE,2=NW,3=W,4=SW,5=SE). */
export function edgeNeighbor(c: number, r: number, i: number): [number, number] {
  const odd = (r & 1) === 1;
  switch (i) {
    case 0: return [c + 1, r];
    case 1: return [odd ? c + 1 : c, r - 1];
    case 2: return [odd ? c : c - 1, r - 1];
    case 3: return [c - 1, r];
    case 4: return [odd ? c : c - 1, r + 1];
    default: return [odd ? c + 1 : c, r + 1];
  }
}

/** hex centre in down-positive world units (vertically foreshortened by ISO_Y so
 *  the grid reads as a tilted-back ground plane — see ISO_Y). */
export function hc(c: number, r: number): [number, number] {
  return [SQ3 * HEX * (c + 0.5 * (r & 1)), 1.5 * HEX * ISO_Y * r];
}

export interface Extent { w: number; h: number; cx: number; cy: number; left: number; top: number; }

/** Board extent including `border` decorative rows top & bottom AND `border`
 *  columns left & right (so the fog shroud overhangs the grid equally on all
 *  four sides). cx/cy stay centred on the play area. */
export function extentOf(g: GameState, border: number): Extent {
  const colW = SQ3 * HEX; // horizontal hex pitch (one column)
  const playW = colW * (g.W + 0.5); // play columns + odd-row half offset
  const w = playW + 2 * border * colW; // overhang `border` columns each side
  const cx = playW / 2 - colW * 0.5; // centre of the play area (border-agnostic)
  // vertical edges are foreshortened by ISO_Y to match hc()'s tilted plane
  const top = (1.5 * HEX * (-border) - HEX) * ISO_Y; // top edge (yDown) of the topmost row
  const bot = (1.5 * HEX * (g.H - 1 + border) + HEX) * ISO_Y; // bottom edge of the lowest row
  const h = bot - top;
  return { w, h, cx, cy: (top + bot) / 2, left: cx - w / 2, top };
}

// ---- sprite URLs --------------------------------------------------------
export function allSpriteUrls(g: GameState): string[] {
  const out: string[] = [];
  for (const side of ['R', 'Y'] as Side[]) {
    const facing = sideFacing(side);
    for (const t of SHIP_TYPES) {
      const art = shipArtUrl(t, facing);
      out.push(art ?? `assets/sprites/ship_${t.toLowerCase()}_${side.toLowerCase()}.png`);
    }
    out.push(ensignUrl(facing));
  }
  if (g.cities.length) out.push('assets/sprites/city.png');
  out.push(
    'assets/sprites/buoy.png', 'assets/sprites/buoy_grey.png',
    'assets/sprites/grass_tile.png',
    'assets/sprites/tree.png', 'assets/sprites/mountain.png',
    'assets/sprites/sea.jpg',
  );
  return [...new Set(out)];
}

// ---- minimap (a small Canvas2D HUD thumbnail — kept on 2D by design) ----
/** Minimap canvas size (px) for a target width, in the board's TRUE hex aspect:
 *  rows are 1.5·r apart with a half-hex margin top & bottom, columns SQ3 wide
 *  plus the odd-row half-offset. Shared with the UI so the zoom controls park
 *  exactly above the minimap. */
export function minimapSize(g: Pick<GameState, 'W' | 'H'>, w: number): { w: number; h: number } {
  const s = w / (SQ3 * (g.W + 0.5)); // px per hex-radius unit
  return { w, h: Math.round((1.5 * (g.H - 1) + 2) * s) };
}

/** Draw the minimap into a (re-usable) canvas using the same pointy-top hex
 *  projection as the main board (odd-row offset, 1.5× row spacing) so tiles line
 *  up with what's on screen instead of a misaligned square grid. */
export function drawMinimap(cv: HTMLCanvasElement, g: GameState, viewSide: Side, w: number, fog = true): void {
  const s = w / (SQ3 * (g.W + 0.5)); // px per hex-radius unit
  const h = minimapSize(g, w).h;
  cv.width = w;
  cv.height = h;
  const x = cv.getContext('2d')!;
  x.fillStyle = MINI.sea;
  x.fillRect(0, 0, w, h);
  const corners = hexCorners(s);
  // hex centre → minimap px (matches hc(): odd rows shift +SQ3/2, half-hex margins)
  const px = (c: number, r: number): number => (SQ3 * (c + 0.5 * (r & 1)) + SQ3 / 2) * s;
  const py = (r: number): number => (1.5 * r + 1) * s;
  const fillHex = (cxp: number, cyp: number, col: string): void => {
    x.fillStyle = col;
    x.beginPath();
    x.moveTo(cxp + corners[0]![0], cyp + corners[0]![1]);
    for (let i = 1; i < 6; i++) x.lineTo(cxp + corners[i]![0], cyp + corners[i]![1]);
    x.closePath();
    x.fill();
  };
  for (let r = 0; r < g.H; r++) {
    for (let c = 0; c < g.W; c++) {
      const t = g.tiles[r]![c];
      if (t === 'land') fillHex(px(c, r), py(r), MINI.land);
    }
  }
  for (const ct of g.cities) {
    // City ownership is always shown (the move log + roster reveal it anyway, so no
    // ownership fog); only the terrain shroud below hides what you haven't scouted.
    fillHex(px(ct.c, ct.r), py(ct.r), ct.owner ? miniCity(ct.owner) : MINI.cityNeutral);
  }
  // Fog of war (mirrors the board shroud): shroud the whole minimap, then punch out
  // the tiles the view side can currently see as hex holes via an even-odd fill.
  // Off on the deploy screen (fog=false), where the whole map must be visible.
  if (fog) {
    x.fillStyle = FOG_FILL;
    x.beginPath();
    x.rect(0, 0, w, h);
    for (let r = 0; r < g.H; r++) {
      for (let c = 0; c < g.W; c++) {
        if (!tileInView(g, viewSide, c, r)) continue;
        const cxp = px(c, r);
        const cyp = py(r);
        x.moveTo(cxp + corners[0]![0], cyp + corners[0]![1]);
        for (let i = 1; i < 6; i++) x.lineTo(cxp + corners[i]![0], cyp + corners[i]![1]);
        x.closePath();
      }
    }
    x.fill('evenodd');
  }
  // Visible ships sit in the punched-out holes — draw them crisp on top of the fog.
  for (const s2 of g.ships) {
    if (!visibleOnBoard(g, viewSide, s2)) continue;
    x.fillStyle = s2.side === 'R' ? MINI.shipR : MINI.shipY;
    x.beginPath();
    x.arc(px(s2.c, s2.r), py(s2.r), Math.max(1.5, s * 0.6), 0, Math.PI * 2);
    x.fill();
  }
}

// ---- board view contract (shared by every renderer + the UI) ------------
export interface BoardOpts {
  width: number;
  height: number;
  viewSide?: Side;
  selectedId?: number;
  /** reachable tiles to mark with buoys: [c, r, canStop] */
  reach?: [number, number, boolean][];
  /** ordered hex path drawn as a dotted route */
  route?: [number, number][];
  /** the selected move's traversed path, drawn as a dotted white trail */
  trail?: [number, number][];
  /** several routes at once (buy/deploy + in-play order lines), drawn DIMMED */
  routes?: [number, number][][];
  /** the selected ship's route, drawn PROMINENT on top of the dimmed `routes` so it
   *  stands out from the rest without hiding them. */
  routeSel?: [number, number][];
  /** run the live render loop (water shimmer + move tweens). Always frozen under
   *  automation (navigator.webdriver) so goldens stay deterministic. */
  animate?: boolean;
  /** Full-window play board: fit on both axes (so a portrait window doesn't clip
   *  the board sides) and allow resize()/panPixels(). Off for the fixed-size
   *  golden scenarios so their framing — and screenshots — stay byte-identical. */
  responsive?: boolean;
  /** the top of the zoom range (the value passed to zoom() at full zoom-in).
   *  Used to ramp narrow/portrait viewports further so that, at max zoom, the
   *  on-screen hex size matches what a wide viewport gets. Defaults to 3. */
  maxZoom?: number;
  /** draw the fog-of-war shroud over tiles the view side can't see. Defaults to
   *  true; turned off on the deploy screen where the whole map must be visible to
   *  plan (you have no ships there yet). */
  fog?: boolean;
}

/** The mutable parts of a board view, swapped on each update(). */
export interface BoardDynamic {
  selectedId?: number | undefined;
  reach?: [number, number, boolean][] | undefined;
  route?: [number, number][] | undefined;
  /** the selected move's traversed path, drawn as a dotted white trail */
  trail?: [number, number][] | undefined;
  routes?: [number, number][][] | undefined;
  /** the selected ship's route, drawn prominent over the dimmed `routes` */
  routeSel?: [number, number][] | undefined;
  /** lone origin dots (a chosen "from" port awaiting its destination) */
  marks?: [number, number][] | undefined;
  /** toggle the fog-of-war shroud (see BoardOpts.fog). Persists across updates
   *  when omitted, so callers set it only when it changes (deploy → play). */
  fog?: boolean | undefined;
}

export interface BoardView {
  renderOnce(): void;
  /** the canvas element; consumers attach pointer/click listeners to it */
  renderer: { domElement: HTMLCanvasElement };
  drawCalls(): number;
  zoom(factor: number): void;
  /** pan the camera by a screen-pixel delta (drag-to-pan); responsive boards only */
  panPixels(dxPx: number, dyPx: number): void;
  /** resize the drawing buffer + camera to a new pixel size (window resize) */
  resize(w: number, h: number): void;
  /** show/hide the Canvas2D minimap thumbnail (bottom-right) */
  setMinimapShown(shown: boolean): void;
  /** rebuild ships/labels/overlays from the (mutated) game + new dynamic opts */
  update(dyn: BoardDynamic): void;
  /** slide a ship sprite along a hex path, then call onDone. Instant (no tween)
   *  under automation or when the live loop is off. */
  animateMove(shipId: number, path: [number, number][], onDone: () => void): void;
  /** stop the render loop + release rAF (call when tearing the board down) */
  dispose(): void;
  /** map a client mouse position to a hex, or null if off the board */
  screenToHex(clientX: number, clientY: number): [number, number] | null;
  /** map a hex to a client pixel position (inverse of screenToHex) */
  hexToScreen(c: number, r: number): [number, number];
}
